import { useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { AnalyticsEvents, track } from '@/lib/services/analytics'
import { getPlanCard } from '@/pages/SettingsPage/components/billing/planConfig'
import { BillingLockedTooltip } from '@/components/billing/BillingLockedTooltip'
import { useTranslation } from 'react-i18next'
import { intlLocaleForUi } from '@/i18n'

export type TrialAudienceUpgradeVariant = 'audience' | 'team-invite'

interface TrialAudienceUpgradeModalProps {
  open: boolean
  onClose: () => void
  /**
   * Surface this upgrade modal is rendered from. Controls copy, the CTA
   * `data-testid`, and the `surface` analytics property.
   * Defaults to `'audience'` to preserve the original entry point.
   */
  variant?: TrialAudienceUpgradeVariant
  /**
   * Accepted for call-site compatibility. Self-serve checkout is locked
   * during the payments-provider migration, so this no longer changes
   * behavior; it drove the switch-vs-checkout branch and will again with
   * Paddle.
   */
  hasActiveSubscription?: boolean
}

type DismissSource = 'button' | 'esc' | 'overlay'

interface VariantConfig {
  titleKey: 'upgrade.audienceTitle' | 'upgrade.inviteTitle'
  descriptionKey: 'upgrade.audienceBody' | 'upgrade.inviteBody'
  ctaTestId: string
  /** Sent as the `surface` property on every analytics event for this modal. */
  surface: string
}

const VARIANTS: Record<TrialAudienceUpgradeVariant, VariantConfig> = {
  audience: {
    titleKey: 'upgrade.audienceTitle',
    descriptionKey: 'upgrade.audienceBody',
    ctaTestId: 'trial-audience-upgrade-pro-cta',
    surface: 'audience',
  },
  'team-invite': {
    titleKey: 'upgrade.inviteTitle',
    descriptionKey: 'upgrade.inviteBody',
    ctaTestId: 'team-invite-upgrade-pro-cta',
    surface: 'team_invite',
  },
}

export function TrialAudienceUpgradeModal({
  open,
  onClose,
  variant = 'audience',
}: TrialAudienceUpgradeModalProps) {
  const { t, i18n } = useTranslation('app')
  const dismissSourceRef = useRef<DismissSource>('button')
  const viewedRef = useRef(false)
  const config = VARIANTS[variant]
  const enterpriseCard = getPlanCard('enterprise')
  const numberLocale = intlLocaleForUi(i18n.language)
  const monthlyPriceUsd = enterpriseCard.monthlyPriceUsd
  const isCustomPrice = monthlyPriceUsd === null
  const enterprisePriceLabel = isCustomPrice
    ? t('billing.custom')
    : `$${monthlyPriceUsd.toLocaleString(numberLocale)}`

  useEffect(() => {
    if (!open) {
      viewedRef.current = false
      return
    }
    if (!viewedRef.current) {
      track(AnalyticsEvents.TRIAL_AUDIENCE_UPGRADE_MODAL_VIEWED, {
        surface: config.surface,
      })
      viewedRef.current = true
    }
  }, [open, config.surface])

  const dismissWith = (source: DismissSource) => {
    track(AnalyticsEvents.TRIAL_AUDIENCE_UPGRADE_DISMISSED, {
      source,
      surface: config.surface,
    })
    onClose()
  }

  // Radix calls onOpenChange(false) for ESC, overlay click, and the X button.
  // ESC + overlay set the source ref ahead of time; anything else (X button or
  // programmatic) lands here as 'button'.
  const handleOpenChange = (next: boolean) => {
    if (next) return
    dismissWith(dismissSourceRef.current)
    dismissSourceRef.current = 'button'
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-sm gap-0 overflow-hidden rounded-2xl bg-background p-0 sm:p-0"
        onEscapeKeyDown={() => {
          dismissSourceRef.current = 'esc'
        }}
        onPointerDownOutside={() => {
          dismissSourceRef.current = 'overlay'
        }}
      >
        <div className="flex flex-col items-center px-6 pb-8 text-center">
          <DialogHeader className="flex w-full flex-col items-center gap-2 pt-8 text-center sm:text-center">
            <DialogTitle className="font-display text-2xl font-normal leading-tight tracking-tight text-foreground">
              {t(config.titleKey)}
            </DialogTitle>
            <DialogDescription className="text-sm leading-snug text-muted-foreground">
              {t(config.descriptionKey)}
            </DialogDescription>
          </DialogHeader>

          <div
            className="mt-6 flex w-full flex-col items-center gap-5 rounded-2xl border border-primary/25 bg-primary/5 px-6 py-6 dark:border-primary/30 dark:bg-primary/10"
            data-testid="trial-audience-upgrade-pro-card"
          >
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-baseline justify-center gap-2">
                <span className="font-display text-5xl font-normal leading-none tracking-tight tabular-nums text-foreground">
                  {enterprisePriceLabel}
                </span>
                {!isCustomPrice && (
                  <Text size="sm" color="muted">
                    {t('billing.perMonth')}
                  </Text>
                )}
              </div>
              {enterpriseCard.priceCaption ? (
                <Text size="xs" color="muted">
                  {t('billing.billedAnnually')}
                </Text>
              ) : null}
            </div>

            {/* Checkout (and its checkout-started analytics) re-wires here once
                the Paddle integration lands. */}
            <BillingLockedTooltip className="w-full">
              <Button
                variant="cta"
                disabled
                className="w-full"
                data-testid={config.ctaTestId}
              >
                {t('upgrade.upgradeNow')}
              </Button>
            </BillingLockedTooltip>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
